import React, { useEffect, useState } from "react";
import {
  fetchMatches,
  createMatch,
  updateMatch,
  deleteMatchApi,
  fetchStats,
  fetchComputedStats,
  updateStats,
  adminLogin,
  adminMe,
  logout,
  getToken,
  fetchPlansConfig,
  updatePlansConfig,
} from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { useToast } from "../hooks/use-toast";
import { LogOut, Plus, Pencil, Trash2, ShieldCheck, Lock, BarChart3, Save } from "lucide-react";
import Logo from "../components/Logo";

const emptyMatch = {
  id: "",
  date: new Date().toISOString().slice(0, 10),
  league: "",
  home: "",
  away: "",
  tip: "",
  odds: 1.9,
  ftResult: "-",
  status: "WIN",
};

const Admin = () => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      if (!getToken()) { setChecking(false); return; }
      try { await adminMe(); setAuthed(true); } catch { logout(); }
      setChecking(false);
    })();
  }, []);

  if (checking) return <div className="min-h-screen bg-ep-royal flex items-center justify-center text-white">Loading...</div>;
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => { logout(); setAuthed(false); }} />;
};

const Login = ({ onSuccess }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(username, password);
      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ep-royal flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo size={44} variant="light" /></div>
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#ff5a5a]/10 flex items-center justify-center">
              <Lock size={20} className="text-[#ff5a5a]" />
            </div>
            <div>
              <h1 className="text-[#0a1a4a] text-xl font-bold">Admin Access</h1>
              <p className="text-[#0a1a4a]/60 text-xs">Explorer Picks control panel</p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="u" className="text-[#0a1a4a] text-sm font-semibold">Username</Label>
              <Input id="u" value={username} onChange={(e) => setUsername(e.target.value)} className="mt-1.5 h-11" autoFocus />
            </div>
            <div>
              <Label htmlFor="p" className="text-[#0a1a4a] text-sm font-semibold">Password</Label>
              <Input id="p" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" />
            </div>
            {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-3 py-2">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full h-11 bg-ep-coral hover:bg-[#ff7676] text-white font-semibold coral-glow">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </div>
        <div className="mt-6 text-center text-white/60 text-xs">Protected area — authorised personnel only.</div>
      </div>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const [statsMode, setMode] = useState("manual");
  const [manual, setManual] = useState({ winningTipsLastYear: 520, activeMembers: 675, averageWinRate: 78 });
  const [computed, setComputed] = useState({ winningTipsLastYear: 0, activeMembers: 675, averageWinRate: 0 });

  const [hero, setHero] = useState({
    yearsOfExpertise: 28,
    activeMembers: 675,
    averageWinRate: 78,
    leaguesCovered: "Almost all leagues covered",
  });

  const [archiveMode, setArchiveMode] = useState("auto");
  const [archiveManual, setArchiveManual] = useState({ totalPicks: 0, wins: 0, losses: 0, winRate: 0 });
  const [archiveComputed, setArchiveComputed] = useState({ totalPicks: 0, wins: 0, losses: 0, winRate: 0 });

  // Plans: 4 offers (trial + 3 plans). status "" = show nothing, undefined here means "use default (Available)"
  const OFFERS = [
    { id: "trial",  name: "TEST MY TIPS",       defaultPrice: 50,  currency: "€" },
    { id: "days10", name: "10 Days Access",     defaultPrice: 100, currency: "€" },
    { id: "days30", name: "30 Days Access",     defaultPrice: 200, currency: "€" },
    { id: "season", name: "Whole Season Pass",  defaultPrice: 800, currency: "€" },
  ];
  const [plansCfg, setPlansCfg] = useState({});

  const loadAll = async () => {
    try {
      const [m, s, c, pc] = await Promise.all([
        fetchMatches(),
        fetchStats(),
        fetchComputedStats(),
        fetchPlansConfig(),
      ]);
      setRows(m);
      setMode(s.mode);
      setManual({
        winningTipsLastYear: s.winningTipsLastYear,
        activeMembers: s.activeMembers,
        averageWinRate: s.averageWinRate,
      });
      if (s.hero) {
        setHero({
          yearsOfExpertise: s.hero.yearsOfExpertise ?? 28,
          activeMembers: s.hero.activeMembers ?? 675,
          averageWinRate: s.hero.averageWinRate ?? 78,
          leaguesCovered: s.hero.leaguesCovered ?? "Almost all leagues covered",
        });
      }
      if (s.archive) {
        setArchiveMode(s.archive.mode || "auto");
        setArchiveManual({
          totalPicks: s.archive.totalPicks ?? 0,
          wins: s.archive.wins ?? 0,
          losses: s.archive.losses ?? 0,
          winRate: s.archive.winRate ?? 0,
        });
      }
      setComputed({
        winningTipsLastYear: c.winningTipsLastYear,
        activeMembers: c.activeMembers,
        averageWinRate: c.averageWinRate,
      });
      if (c.archive) setArchiveComputed(c.archive);
      // Build editable plans state — default status "Available" if unset
      const draft = {};
      OFFERS.forEach((o) => {
        const stored = pc[o.id] || {};
        draft[o.id] = {
          price: typeof stored.price === "number" ? stored.price : o.defaultPrice,
          status: typeof stored.status === "string" ? stored.status : "Available",
        };
      });
      setPlansCfg(draft);
    } catch (e) {
      // handle
    }
  };

  useEffect(() => {
    loadAll();
    const h = () => loadAll();
    window.addEventListener("ep-data-updated", h);
    return () => window.removeEventListener("ep-data-updated", h);
  }, []);

  const openNew = () => { setEditing({ ...emptyMatch, id: "" }); setOpen(true); };
  const openEdit = (m) => { setEditing({ ...m }); setOpen(true); };

  const save = async () => {
    if (!editing.home || !editing.away || !editing.tip) {
      toast({ title: "Missing fields", description: "Home, away, and pick are required." });
      return;
    }
    const payload = {
      date: editing.date, league: editing.league || "", home: editing.home, away: editing.away,
      tip: editing.tip, odds: parseFloat(editing.odds) || 0, ftResult: editing.ftResult || "-",
      status: editing.status,
    };
    try {
      if (editing.id) await updateMatch(editing.id, payload);
      else await createMatch(payload);
      setOpen(false);
      await loadAll();
      toast({ title: "Saved", description: "Match has been saved to the archive." });
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Save failed." });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this match from the archive?")) return;
    try {
      await deleteMatchApi(id);
      await loadAll();
      toast({ title: "Deleted", description: "Match removed from the archive." });
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Delete failed." });
    }
  };

  const toggleMode = async (v) => {
    const m = v ? "manual" : "auto";
    setMode(m);
    try {
      await updateStats({ mode: m, manual });
      toast({ title: "Mode updated", description: `Stats mode set to ${m}.` });
    } catch (e) {
      toast({ title: "Error", description: "Could not update mode." });
    }
  };

  const saveStats = async () => {
    try {
      await updateStats({ mode: "manual", manual });
      toast({ title: "Stats saved", description: "Homepage statistics updated." });
    } catch (e) {
      toast({ title: "Error", description: "Could not save stats." });
    }
  };

  const saveHero = async () => {
    try {
      await updateStats({ hero });
      toast({ title: "Hero saved", description: "Hero trust chips updated." });
    } catch (e) {
      toast({ title: "Error", description: "Could not save hero values." });
    }
  };

  const toggleArchiveMode = async (v) => {
    const m = v ? "manual" : "auto";
    setArchiveMode(m);
    try {
      await updateStats({ archive: { mode: m, manual: archiveManual } });
      toast({ title: "Archive mode updated", description: `Archive stats set to ${m}.` });
    } catch (e) {
      toast({ title: "Error", description: "Could not update archive mode." });
    }
  };

  const saveArchiveStats = async () => {
    try {
      await updateStats({ archive: { mode: "manual", manual: archiveManual } });
      toast({ title: "Archive stats saved", description: "Archive numbers updated." });
    } catch (e) {
      toast({ title: "Error", description: "Could not save archive stats." });
    }
  };

  const savePlans = async () => {
    try {
      const payload = {};
      OFFERS.forEach((o) => {
        const v = plansCfg[o.id] || {};
        const priceNum = typeof v.price === "number" ? v.price : parseFloat(v.price);
        payload[o.id] = {
          price: isNaN(priceNum) ? o.defaultPrice : priceNum,
          status: typeof v.status === "string" ? v.status : "",
        };
      });
      await updatePlansConfig(payload);
      toast({ title: "Offers saved", description: "Prices and statuses updated live." });
    } catch (e) {
      toast({ title: "Error", description: "Could not save offers." });
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7ff]">
      <div className="bg-ep-royal border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={36} variant="light" />
            <div className="hidden sm:block h-8 w-px bg-white/20 mx-2" />
            <div className="hidden sm:flex items-center gap-2 text-white/80 text-sm">
              <ShieldCheck size={16} className="text-[#ff5a5a]" /> Admin Panel
            </div>
          </div>
          <Button onClick={onLogout} variant="outline" className="bg-transparent border-white/25 text-white hover:bg-white/10">
            <LogOut size={16} className="mr-2" /> Sign out
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 space-y-8">
        {/* Stats */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2">
                <BarChart3 size={20} className="text-[#ff5a5a]" />
                <h2 className="text-[#0a1a4a] text-lg font-bold">Homepage Statistics</h2>
              </div>
              <p className="text-[#0a1a4a]/60 text-sm mt-1">
                Auto-calculate from archive or set manually.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-[#0a1a4a]/70">Auto</span>
              <Switch checked={statsMode === "manual"} onCheckedChange={toggleMode} />
              <span className="text-sm font-medium text-[#0a1a4a]/70">Manual</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <StatField label="Winning picks last year"
              value={statsMode === "manual" ? manual.winningTipsLastYear : computed.winningTipsLastYear}
              disabled={statsMode !== "manual"}
              onChange={(v) => setManual({ ...manual, winningTipsLastYear: parseInt(v) || 0 })} />
            <StatField label="Active members"
              value={statsMode === "manual" ? manual.activeMembers : computed.activeMembers}
              disabled={statsMode !== "manual"}
              onChange={(v) => setManual({ ...manual, activeMembers: parseInt(v) || 0 })} />
            <StatField label="Average win rate (%)"
              value={statsMode === "manual" ? manual.averageWinRate : computed.averageWinRate}
              disabled={statsMode !== "manual"}
              onChange={(v) => setManual({ ...manual, averageWinRate: parseInt(v) || 0 })} />
          </div>
          {statsMode === "manual" && (
            <div className="mt-4 flex justify-end">
              <Button onClick={saveStats} className="bg-ep-coral hover:bg-[#ff7676] text-white font-semibold">
                <Save size={16} className="mr-2" /> Save stats
              </Button>
            </div>
          )}
          {statsMode === "auto" && (
            <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-[#0a1a4a]/70">
              Currently auto-calculating from archive data — wins: <b>{computed.winningTipsLastYear}</b>, win rate: <b>{computed.averageWinRate}%</b>.
            </div>
          )}
        </div>

        {/* Hero Trust Chips */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2">
                <BarChart3 size={20} className="text-[#ff5a5a]" />
                <h2 className="text-[#0a1a4a] text-lg font-bold">Hero Trust Chips</h2>
              </div>
              <p className="text-[#0a1a4a]/60 text-sm mt-1">
                The four numbers shown right below the hero headline on the home page.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <StatField
              label="Years of expertise"
              value={hero.yearsOfExpertise}
              onChange={(v) => setHero({ ...hero, yearsOfExpertise: parseInt(v) || 0 })}
            />
            <StatField
              label="Active members"
              value={hero.activeMembers}
              onChange={(v) => setHero({ ...hero, activeMembers: parseInt(v) || 0 })}
            />
            <StatField
              label="Average win rate (%)"
              value={hero.averageWinRate}
              onChange={(v) => setHero({ ...hero, averageWinRate: parseInt(v) || 0 })}
            />
            <TextField
              label="Leagues covered (text)"
              value={hero.leaguesCovered}
              onChange={(v) => setHero({ ...hero, leaguesCovered: v })}
            />
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={saveHero} className="bg-ep-coral hover:bg-[#ff7676] text-white font-semibold">
              <Save size={16} className="mr-2" /> Save hero chips
            </Button>
          </div>
        </div>

        {/* Archive Statistics */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2">
                <BarChart3 size={20} className="text-[#ff5a5a]" />
                <h2 className="text-[#0a1a4a] text-lg font-bold">Archive Page Statistics</h2>
              </div>
              <p className="text-[#0a1a4a]/60 text-sm mt-1">
                The four cards at the top of the /archive page. Auto-calculate from matches or override manually.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-[#0a1a4a]/70">Auto</span>
              <Switch checked={archiveMode === "manual"} onCheckedChange={toggleArchiveMode} />
              <span className="text-sm font-medium text-[#0a1a4a]/70">Manual</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <StatField
              label="Total picks"
              value={archiveMode === "manual" ? archiveManual.totalPicks : archiveComputed.totalPicks}
              disabled={archiveMode !== "manual"}
              onChange={(v) => setArchiveManual({ ...archiveManual, totalPicks: parseInt(v) || 0 })}
            />
            <StatField
              label="Wins"
              value={archiveMode === "manual" ? archiveManual.wins : archiveComputed.wins}
              disabled={archiveMode !== "manual"}
              onChange={(v) => setArchiveManual({ ...archiveManual, wins: parseInt(v) || 0 })}
            />
            <StatField
              label="Losses"
              value={archiveMode === "manual" ? archiveManual.losses : archiveComputed.losses}
              disabled={archiveMode !== "manual"}
              onChange={(v) => setArchiveManual({ ...archiveManual, losses: parseInt(v) || 0 })}
            />
            <StatField
              label="Win rate (%)"
              value={archiveMode === "manual" ? archiveManual.winRate : archiveComputed.winRate}
              disabled={archiveMode !== "manual"}
              onChange={(v) => setArchiveManual({ ...archiveManual, winRate: parseInt(v) || 0 })}
            />
          </div>
          {archiveMode === "manual" && (
            <div className="mt-4 flex justify-end">
              <Button onClick={saveArchiveStats} className="bg-ep-coral hover:bg-[#ff7676] text-white font-semibold">
                <Save size={16} className="mr-2" /> Save archive stats
              </Button>
            </div>
          )}
          {archiveMode === "auto" && (
            <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-[#0a1a4a]/70">
              Currently auto-calculating from all matches — total: <b>{archiveComputed.totalPicks}</b>, wins: <b>{archiveComputed.wins}</b>, losses: <b>{archiveComputed.losses}</b>, win rate: <b>{archiveComputed.winRate}%</b>.
            </div>
          )}
        </div>

        {/* Subscription Offers */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2">
                <BarChart3 size={20} className="text-[#ff5a5a]" />
                <h2 className="text-[#0a1a4a] text-lg font-bold">Subscription Offers</h2>
              </div>
              <p className="text-[#0a1a4a]/60 text-sm mt-1">
                Set the price and status shown for each offer. Leave the status blank to hide it completely on the site.
              </p>
            </div>
          </div>

          <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-[#0a1a4a]/70">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Offer</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider w-40">Price</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {OFFERS.map((o) => {
                  const cur = plansCfg[o.id] || { price: o.defaultPrice, status: "Available" };
                  return (
                    <tr key={o.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-[#0a1a4a] text-sm">{o.name}</div>
                        <div className="text-[#0a1a4a]/50 text-xs mt-0.5">Default {o.currency}{o.defaultPrice}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-[#0a1a4a] font-bold">{o.currency}</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={cur.price ?? ""}
                            onChange={(e) =>
                              setPlansCfg({
                                ...plansCfg,
                                [o.id]: { ...cur, price: e.target.value === "" ? "" : parseFloat(e.target.value) },
                              })
                            }
                            className="h-10 w-28 bg-white text-[#0a1a4a] font-semibold"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          type="text"
                          value={cur.status ?? ""}
                          placeholder="Leave blank to hide"
                          onChange={(e) =>
                            setPlansCfg({
                              ...plansCfg,
                              [o.id]: { ...cur, status: e.target.value },
                            })
                          }
                          className="h-10 bg-white text-[#0a1a4a] font-semibold"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex justify-end">
            <Button onClick={savePlans} className="bg-ep-coral hover:bg-[#ff7676] text-white font-semibold">
              <Save size={16} className="mr-2" /> Save offers
            </Button>
          </div>
        </div>

        {/* Matches */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <div>
              <h2 className="text-[#0a1a4a] text-lg font-bold">Predictions Archive</h2>
              <p className="text-[#0a1a4a]/60 text-sm mt-1">
                Add, edit or delete matches. Changes appear live across the site.
              </p>
            </div>
            <Button onClick={openNew} className="bg-ep-coral hover:bg-[#ff7676] text-white font-semibold coral-glow">
              <Plus size={16} className="mr-2" /> Add match
            </Button>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-[#0a1a4a]/70">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Match</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">League</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Pick</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Odds</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">FT</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-sm text-[#0a1a4a]/70 whitespace-nowrap">{m.date}</td>
                    <td className="px-4 py-3 text-sm text-[#0a1a4a] font-semibold">
                      {m.home} <span className="text-[#0a1a4a]/40">vs</span> {m.away}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#0a1a4a]/60 hidden md:table-cell">{m.league}</td>
                    <td className="px-4 py-3 text-sm text-[#0a1a4a]">{m.tip}</td>
                    <td className="px-4 py-3 text-sm text-[#0a1a4a]/70">{m.odds}</td>
                    <td className="px-4 py-3 text-sm text-[#0a1a4a]/70">{m.ftResult}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-1 rounded ${
                        m.status === "WIN" ? "bg-emerald-100 text-emerald-700" :
                        m.status === "LOSS" ? "bg-rose-100 text-rose-700" :
                        "bg-slate-100 text-slate-700"
                      }`}>{m.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => openEdit(m)} className="p-2 rounded-lg hover:bg-slate-100 text-[#0a1a4a]"><Pencil size={16} /></button>
                        <button onClick={() => remove(m.id)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-600"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit match" : "Add new match"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid md:grid-cols-2 gap-4 py-2">
              <FormField label="Date"><Input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></FormField>
              <FormField label="League"><Input value={editing.league} onChange={(e) => setEditing({ ...editing, league: e.target.value })} placeholder="e.g. England: Premier League" /></FormField>
              <FormField label="Home team"><Input value={editing.home} onChange={(e) => setEditing({ ...editing, home: e.target.value })} /></FormField>
              <FormField label="Away team"><Input value={editing.away} onChange={(e) => setEditing({ ...editing, away: e.target.value })} /></FormField>
              <FormField label="Pick"><Input value={editing.tip} onChange={(e) => setEditing({ ...editing, tip: e.target.value })} placeholder="e.g. Over 2.5" /></FormField>
              <FormField label="Odds"><Input type="number" step="0.01" value={editing.odds} onChange={(e) => setEditing({ ...editing, odds: e.target.value })} /></FormField>
              <FormField label="FT Result"><Input value={editing.ftResult} onChange={(e) => setEditing({ ...editing, ftResult: e.target.value })} placeholder="e.g. 2-1" /></FormField>
              <FormField label="Status">
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WIN">WIN</SelectItem>
                    <SelectItem value="LOSS">LOSS</SelectItem>
                    <SelectItem value="DRAW">DRAW</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-ep-coral hover:bg-[#ff7676] text-white font-semibold">Save match</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FormField = ({ label, children }) => (
  <div>
    <label className="text-[#0a1a4a] text-sm font-semibold mb-1.5 block">{label}</label>
    {children}
  </div>
);

const StatField = ({ label, value, disabled, onChange }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
    <div className="text-xs uppercase tracking-wider text-[#0a1a4a]/60 font-semibold mb-1.5">{label}</div>
    <Input
      type="number"
      value={value ?? 0}
      disabled={disabled}
      onChange={(e) => onChange && onChange(e.target.value)}
      className="h-11 bg-white text-lg font-bold text-[#0a1a4a]"
    />
  </div>
);

const TextField = ({ label, value, disabled, onChange }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
    <div className="text-xs uppercase tracking-wider text-[#0a1a4a]/60 font-semibold mb-1.5">{label}</div>
    <Input
      type="text"
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange && onChange(e.target.value)}
      className="h-11 bg-white text-sm font-semibold text-[#0a1a4a]"
    />
  </div>
);

export default Admin;
